import { Injectable } from '@angular/core';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import type { MessageFolderSummaryDto } from '@sharedlib/dtos/dto/mail-app/folder.dto';
import type { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { type Observable, Subject } from 'rxjs';

/** Where an open came from: the list's own rows, or a suggestion in the search popup (searchopen r2). */
export type MailReaderOpenOrigin = 'list' | 'search';

export interface MailReaderOpenRequest {
  readonly item: MessageSummaryDto | MessageFolderSummaryDto;
  /** The tenant to read the item under, when it is not the mailbox on screen (a suggestion from the other scope). */
  readonly config?: StitchRequestConfig;
  readonly origin: MailReaderOpenOrigin;
}

export interface MailReaderOpened {
  readonly id: string;
  readonly origin: MailReaderOpenOrigin;
}

/**
 * The ONE way into Mail's reader (the third panel). The list container runs the open — the skeleton from the summary
 * at once, the read, then the reader — on what arrives here, so a list click and the search popup's "Open email"
 * share it: the same skeleton in the same frame, the same read, and the later of two opens wins (searchopen r2,
 * Pavel 2026-10-29: "we should probably re-use same path/code to invoke 3rd panel").
 */
@Injectable()
export class MailReaderOpenService {
  private readonly requests = new Subject<MailReaderOpenRequest>();
  public readonly requests$: Observable<MailReaderOpenRequest> = this.requests.asObservable();
  private readonly opened = new Subject<MailReaderOpened>();
  /**
   * An open that reached the reader. A failed one (a stale suggestion's 404) never emits, so whatever follows the
   * reader — the list's selection — stays with the item still on screen (Codex r4 P2 on #2758, reproduced live).
   */
  public readonly opened$: Observable<MailReaderOpened> = this.opened.asObservable();

  public open(
    item: MessageSummaryDto | MessageFolderSummaryDto,
    config?: StitchRequestConfig,
    origin: MailReaderOpenOrigin = 'list',
  ): void {
    this.requests.next({ item, config, origin });
  }

  /** Called by the one runner of the open, once the item is in the reader. */
  public markOpened(id: string, origin: MailReaderOpenOrigin): void {
    this.opened.next({ id, origin });
  }
}
